import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { CheckCircle, XCircle, Clock, AlertCircle, Globe } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useRoles } from '@/hooks/useRoles';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface ToolResult {
  name: string;
  status: 'success' | 'error' | 'skipped';
  error?: string;
  reason?: string;
  logo?: string;
}

interface ScrapeResponse {
  success: boolean;
  processed: number;
  results: ToolResult[];
  summary: {
    success: number;
    errors: number;
    skipped: number;
  };
}

interface ToolsResourcesResult {
  name: string;
  status: 'success' | 'error' | 'skipped';
  error?: string;
  reason?: string;
}

interface ToolsResourcesResponse {
  success: boolean;
  processed: number;
  tools_added: number;
  resources_added: number;
  results: ToolsResourcesResult[];
  summary: {
    tools: { success: number; errors: number; skipped: number };
    resources: { success: number; errors: number; skipped: number };
  };
}

export default function AdminTools() {
  const { toast } = useToast();
  const { isAdmin, loading: rolesLoading } = useRoles();
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [scrapeResults, setScrapeResults] = useState<ScrapeResponse | null>(null);
  
  // New state for toools.design scraping
  const [isToolsLoading, setIsToolsLoading] = useState(false);
  const [toolsProgress, setToolsProgress] = useState(0);
  const [toolsResults, setToolsResults] = useState<ToolsResourcesResponse | null>(null);
  const [dryRun, setDryRun] = useState(true);
  const [maxPages, setMaxPages] = useState(25);
  const [maxItems, setMaxItems] = useState(50);

  // Check if user is admin
  if (rolesLoading) {
    return (
      <div className="container mx-auto py-8 px-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAdmin()) {
    return (
      <div className="container mx-auto py-8 px-4">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Access denied. This page is restricted to administrators only.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const handleScrapeTools = async () => {
    setIsLoading(true);
    setProgress(0);
    setScrapeResults(null);

    try {
      console.log('Starting tool scraping process...');
      setProgress(20);

      const { data, error } = await supabase.functions.invoke('scrape-design-tools', {
        body: {}
      });

      setProgress(80);

      if (error) {
        throw error;
      }

      setProgress(100);
      setScrapeResults(data);

      toast({
        title: "Tool Scraping Complete",
        description: `Successfully processed ${data.processed} tools. ${data.summary.success} added, ${data.summary.skipped} skipped.`,
        duration: 5000,
      });

    } catch (error) {
      console.error('Error scraping tools:', error);
      toast({
        title: "Error",
        description: "Failed to scrape design tools. Please try again.",
        variant: "destructive",
        duration: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleScrapeToolsDesign = async () => {
    setIsToolsLoading(true);
    setToolsProgress(0);
    setToolsResults(null);

    try {
      console.log(`Starting toools.design scraping process... (${dryRun ? 'dry-run' : 'live'} mode)`);
      setToolsProgress(20);

      const { data, error } = await supabase.functions.invoke('scrape-toools-design', {
        body: { 
          dry_run: dryRun,
          max_pages: maxPages,
          max_items: maxItems,
          delay_ms: 1500 // Respectful crawling delay
        }
      });

      setToolsProgress(80);

      if (error) {
        throw error;
      }

      setToolsProgress(100);
      setToolsResults(data);

      const mode = dryRun ? 'analyzed' : 'processed';
      toast({
        title: `Toools.Design Scraping Complete`,
        description: `Successfully ${mode} ${data.processed} items. ${data.tools_added} tools and ${data.resources_added} resources ${dryRun ? 'would be' : 'were'} added.`,
        duration: 5000,
      });

    } catch (error) {
      console.error('Error scraping toools.design:', error);
      toast({
        title: "Error",
        description: "Failed to scrape toools.design. Please try again.",
        variant: "destructive",
        duration: 5000,
      });
    } finally {
      setIsToolsLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'error':
        return <XCircle className="h-4 w-4 text-red-600" />;
      case 'skipped':
        return <AlertCircle className="h-4 w-4 text-yellow-600" />;
      default:
        return <Clock className="h-4 w-4 text-gray-600" />;
    }
  };

  const getStatusText = (result: ToolResult) => {
    switch (result.status) {
      case 'success':
        return `Added successfully${result.logo === 'found' ? ' with logo' : ' (no logo)'}`;
      case 'error':
        return `Error: ${result.error}`;
      case 'skipped':
        return result.reason === 'slug_exists' ? 'Already exists (similar name)' : 'Skipped';
      default:
        return 'Processing...';
    }
  };

  return (
    <div className="container mx-auto p-6 max-w-4xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-foreground mb-2">Design Tools Management</h1>
        <p className="text-muted-foreground">
          Scrape and add top UI/UX design tools and resources to the database.
        </p>
      </div>

      <div className="grid gap-6 mb-6">
        <Card>
          <CardHeader>
            <CardTitle>Scrape Design Tools</CardTitle>
            <CardDescription>
              Add ~100 curated UI/UX and Product Design tools to the database. 
              This process will only add tools that don't already exist and will fetch their logos.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {isLoading && (
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">Scraping tools and fetching logos...</p>
                <Progress value={progress} className="w-full" />
              </div>
            )}
            
            <Button
              onClick={handleScrapeTools}
              disabled={isLoading || isToolsLoading}
              className="w-full"
            >
              {isLoading ? "Scraping Tools..." : "Start Tool Scraping"}
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5" />
              Scrape Toools.Design
            </CardTitle>
            <CardDescription>
              Extract tools and resources from toools.design website. 
              This will analyze the site and add both design tools and resources to the database.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="max-pages" className="text-sm font-medium block mb-1">
                  Max Pages
                </label>
                <Input
                  id="max-pages"
                  type="number"
                  value={maxPages}
                  onChange={(e) => setMaxPages(Number(e.target.value))}
                  min="5"
                  max="100"
                  className="w-full"
                  disabled={isToolsLoading}
                />
              </div>
              <div>
                <label htmlFor="max-items" className="text-sm font-medium block mb-1">
                  Max Items
                </label>
                <Input
                  id="max-items"
                  type="number"
                  value={maxItems}
                  onChange={(e) => setMaxItems(Number(e.target.value))}
                  min="10"
                  max="200"
                  className="w-full"
                  disabled={isToolsLoading}
                />
              </div>
            </div>
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div className="space-y-1">
                <p className="text-sm font-medium">Dry Run Mode</p>
                <p className="text-xs text-muted-foreground">
                  {dryRun ? 'Analyze only - no data will be added' : 'Live mode - data will be added to database'}
                </p>
              </div>
              <Switch
                checked={dryRun}
                onCheckedChange={setDryRun}
                disabled={isToolsLoading}
              />
            </div>

            {isToolsLoading && (
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  {dryRun ? 'Analyzing toools.design...' : 'Scraping toools.design and adding data...'}
                </p>
                <Progress value={toolsProgress} className="w-full" />
              </div>
            )}
            
            <Button
              onClick={handleScrapeToolsDesign}
              disabled={isLoading || isToolsLoading}
              className="w-full"
              variant={dryRun ? "outline" : "default"}
            >
              {isToolsLoading ? 
                (dryRun ? "Analyzing..." : "Scraping...") : 
                (dryRun ? "Analyze Toools.Design" : "Scrape Toools.Design")
              }
            </Button>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6">
        {scrapeResults && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                Design Tools Scraping Results
                <Badge variant="outline">
                  {scrapeResults.processed} tools processed
                </Badge>
              </CardTitle>
              <CardDescription>
                <div className="flex gap-4 text-sm">
                  <span className="text-green-600">✓ {scrapeResults.summary.success} added</span>
                  <span className="text-yellow-600">⚠ {scrapeResults.summary.skipped} skipped</span>
                  <span className="text-red-600">✗ {scrapeResults.summary.errors} errors</span>
                </div>
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {scrapeResults.results.map((result, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 border rounded-lg">
                    {getStatusIcon(result.status)}
                    <div className="flex-1">
                      <p className="font-medium text-sm">{result.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {getStatusText(result)}
                      </p>
                    </div>
                    <Badge 
                      variant={
                        result.status === 'success' ? 'default' : 
                        result.status === 'error' ? 'error' : 'secondary'
                      }
                      className="text-xs"
                    >
                      {result.status}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {toolsResults && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5" />
                Toools.Design Results
                <Badge variant="outline">
                  {toolsResults.processed} items {dryRun ? 'analyzed' : 'processed'}
                </Badge>
              </CardTitle>
              <CardDescription>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="font-medium mb-1">Tools</p>
                    <div className="flex gap-2">
                      <span className="text-green-600">✓ {toolsResults.summary.tools.success}</span>
                      <span className="text-yellow-600">⚠ {toolsResults.summary.tools.skipped}</span>
                      <span className="text-red-600">✗ {toolsResults.summary.tools.errors}</span>
                    </div>
                  </div>
                  <div>
                    <p className="font-medium mb-1">Resources</p>
                    <div className="flex gap-2">
                      <span className="text-green-600">✓ {toolsResults.summary.resources.success}</span>
                      <span className="text-yellow-600">⚠ {toolsResults.summary.resources.skipped}</span>
                      <span className="text-red-600">✗ {toolsResults.summary.resources.errors}</span>
                    </div>
                  </div>
                </div>
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {toolsResults.results.map((result, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 border rounded-lg">
                    {getStatusIcon(result.status)}
                    <div className="flex-1">
                      <p className="font-medium text-sm">{result.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {result.status === 'success' ? `${dryRun ? 'Would be added' : 'Added successfully'}` :
                         result.status === 'error' ? `Error: ${result.error}` :
                         result.reason || 'Skipped'}
                      </p>
                    </div>
                    <Badge 
                      variant={
                        result.status === 'success' ? 'default' : 
                        result.status === 'error' ? 'error' : 'secondary'
                      }
                      className="text-xs"
                    >
                      {result.status}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}